import { getApiBaseUrl } from '../utils/apiBaseUrl';
import fetchWithTimeout from '../utils/fetchWithTimeout';
const CART_ID_STORAGE_KEY = 'checkout.cartId';

let cachedCartId = null;

const getStoredCartId = () => {
  if (cachedCartId) {
    return cachedCartId;
  }
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  try {
    const value = window.localStorage.getItem(CART_ID_STORAGE_KEY);
    cachedCartId = value || null;
    return cachedCartId;
  } catch (error) {
    console.warn('[checkoutApi] Unable to read stored cart id', error);
    return null;
  }
};

const persistCartId = (nextId) => {
  cachedCartId = nextId || null;
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  try {
    if (nextId) {
      window.localStorage.setItem(CART_ID_STORAGE_KEY, nextId);
    } else {
      window.localStorage.removeItem(CART_ID_STORAGE_KEY);
    }
  } catch (error) {
    console.warn('[checkoutApi] Unable to store cart id', error);
  }
};

const syncCartIdFromResponse = (response, payload) => {
  try {
    const headerValue = response.headers.get('x-checkout-cart-id');
    if (headerValue) {
      persistCartId(headerValue);
      return;
    }
  } catch (error) {
    console.warn('[checkoutApi] Failed to read cart id header', error);
  }
  if (payload && typeof payload.id === 'string' && payload.id.trim()) {
    persistCartId(payload.id.trim());
  }
};

const buildHeaders = (token, extraHeaders = {}) => {
  const headers = new Headers(extraHeaders);
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
};

const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await response.json() : null;
  if (!response.ok) {
    const message = payload?.message || payload?.error || response.statusText;
    const error = new Error(message);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  syncCartIdFromResponse(response, payload);
  return payload;
};

const request = async (path, { method = 'GET', body, token, headers = {} } = {}) => {
  const apiUrl = getApiBaseUrl();
  if (!apiUrl) {
    throw new Error('REACT_APP_API_URL is not configured');
  }
  const url = `${apiUrl}${path}`;
  const init = {
    method,
    credentials: 'include',
    headers: buildHeaders(token, headers),
  };
  const storedCartId = getStoredCartId();
  if (storedCartId) {
    init.headers.set('X-Checkout-Cart-Id', storedCartId);
  }
  if (body !== undefined) {
    init.body = typeof body === 'string' ? body : JSON.stringify(body);
  } else if (method === 'GET') {
    init.headers.delete('Content-Type');
  }
  const response = await fetchWithTimeout(url, init);
  return handleResponse(response);
};

export const fetchCatalog = (token, promoCode) => {
  const path = promoCode
    ? `/checkout/catalog?promoCode=${encodeURIComponent(promoCode)}`
    : '/checkout/catalog';
  return request(path, { token, method: 'GET' });
};

// Validate a promo code and return updated catalog with discounts applied
export const validatePromoCode = (promoCode, token) => {
  if (!promoCode || typeof promoCode !== 'string' || !promoCode.trim()) {
    return Promise.reject(new Error('Please enter a promo code'));
  }
  return fetchCatalog(token, promoCode.trim());
};
export const getPaymentMethodAvailability = (token) => request('/stripe/payment-method-availability', { method: 'GET', token });
export const fetchCart = (token) => request('/checkout/cart', { token, method: 'GET' });
export const replaceCart = (payload, token) => request('/checkout/cart', { method: 'PUT', body: payload, token });
export const addCartItems = (payload, token) => request('/checkout/cart/items', { method: 'POST', body: payload, token });
export const patchCartItem = (productSlug, payload, token) => request(`/checkout/cart/items/${encodeURIComponent(productSlug)}`, { method: 'PATCH', body: payload, token });
export const removeCartItem = (productSlug, token) => request(`/checkout/cart/items/${encodeURIComponent(productSlug)}`, { method: 'DELETE', token });
export const clearCart = (token) => request('/checkout/cart', { method: 'DELETE', token });

export const listSavedPaymentMethods = (token) => request('/stripe/saved-payment-methods', { method: 'GET', token });
export const deleteSavedPaymentMethod = (id, token) => request(`/stripe/saved-payment-methods/${encodeURIComponent(id)}`, { method: 'DELETE', token });
export const setDefaultPaymentMethod = (id, token) => request(`/stripe/saved-payment-methods/${encodeURIComponent(id)}/default`, { method: 'POST', token });
